import { useCallback } from 'react';
import { captureException } from '@sentry/react';
import { useDashboardListDispatch } from '../contexts/DashboardListContext';
import { useMarketplaceDispatch } from '../contexts/MarketplaceContext';
import { useDashboardCategoriesDispatch } from '../contexts/single-dashboard/DashboardCategoriesContext';

// payload: title, AccountId, UserId, description
export function useCreateDashboard() {
  async function createDashboard(payload) {
    try {
      const response = await fetch(`/api/dashboards`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        if (payload.newDashboardInfo.embedUrl || data?.reachedLimit) {
          return { message: data.message || 'An error occurred' };
        }
        const error = new Error(data.message || 'Error creating dashboard');
        console.error('Error creating dashboard:', data.message || data);
        captureException(error, { tags: { context: 'createDashboard' } });
        return { message: data.message || 'An error occurred' };
      }

      if (!data?.id) {
        throw new Error('Invalid data received from server.');
      }

      return data;
    } catch (error) {
      console.error('Error creating dashboard:', error);
      captureException(error, { tags: { context: 'createDashboard_network' } });
      return {
        message:
          error.message || 'An error occurred while creating the dashboard.',
      };
    }
  }
  return { createDashboard };
}
// should i separate the api call for published vs unpublished dashboards
export function useUpdateDashboard() {
  const dashboardDispatch = useDashboardListDispatch();
  const dashboardCatDispatch = useDashboardCategoriesDispatch();
  const marketplaceDispatch = useMarketplaceDispatch();

  async function updateDashboard(payload) {
    try {
      const res = await fetch(`/api/dashboards/${payload.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        const error = new Error(data.message || 'Error saving dashboard');
        console.error('Error saving dashboard:', data.message || data);
        captureException(error, { tags: { context: 'updateDashboard' } });
        return { message: data.message || 'An error occurred', ...data };
      }

      if (!data?.id) {
        throw new Error('Invalid data received from server.');
      }

      dashboardDispatch({ type: 'updateDashboard', payload: data });
      // update context to include new dashboard template for accessing in marketplace
      if (payload.isPublishedToMarketplace) {
        marketplaceDispatch({ type: 'addDashboardTemplate', payload: data });
        dashboardCatDispatch({ type: 'setDashCategories', payload: data });
      }

      return data;
    } catch (error) {
      console.error('Error saving dashboard:', error);
      captureException(error, { tags: { context: 'updateDashboard_network' } });
      return {
        message:
          error.message || 'An error occurred while saving the dashboard.',
      };
    }
  }
  return { updateDashboard };
}

//delete a dashboard, payload is the dashboard id
export function useDeleteDashboard() {
  const dashboardDispatch = useDashboardListDispatch();
  async function deleteDashboard(id) {
    try {
      const response = await fetch(`/api/dashboards/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        const error = new Error(data.message || 'Error deleting dashboard');
        console.error('Error deleting dashboard:', data.message || data);
        captureException(error, { tags: { context: 'deleteDashboard' } });
        return { message: data.message || 'An error occurred', ...data };
      }

      dashboardDispatch({ type: 'removeDashboard', payload: data });
      return data;
    } catch (error) {
      console.error('Network error while deleting dashboard:', error);
      captureException(error, { tags: { context: 'deleteDashboard_network' } });
      return {
        message: 'A network error occurred while deleting the dashboard.',
      };
    }
  }
  return deleteDashboard;
}

//delete a native dashboard, payload is the dashboard id
export function useDeleteNativeDashboard() {
  const dashboardDispatch = useDashboardListDispatch();
  async function deleteDashboard(id) {
    try {
      const response = await fetch(`/api/dashboards/native/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      const data = await response.json();

      if (!response.ok) {
        const error = new Error(
          data.message || 'Error deleting native dashboard'
        );
        console.error('Error deleting native dashboard:', data.message || data);
        captureException(error, { tags: { context: 'deleteNativeDashboard' } });
        return { message: data.message || 'An error occurred', ...data };
      }

      dashboardDispatch({ type: 'removeDashboard', payload: data });
      return data;
    } catch (error) {
      console.error('Network error while deleting dashboard:', error);
      captureException(error, {
        tags: { context: 'deleteNativeDashboard_network' },
      });
      return {
        message: 'A network error occurred while deleting the dashboard.',
      };
    }
  }
  return deleteDashboard;
}

/**
 * Hook for auto-creating a dashboard after CSV upload (IW-5283)
 * Called automatically after successful file upload to trigger dashboard creation.
 *
 * @returns {Object} { autoCreateDashboard } - Function to auto-create dashboard
 */
export function useAutoCreateDashboard() {
  const autoCreateDashboard = useCallback(
    async ({ schoolId, metricCategory }) => {
      try {
        const response = await fetch(`/api/ciwp/dashboards/auto-create`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ schoolId, metricCategory }),
        });

        const data = await response.json();

        if (!response.ok) {
          const error = new Error(
            data.message || 'Error auto-creating dashboard'
          );
          console.error('Error auto-creating dashboard:', data.message || data);
          captureException(error, { tags: { context: 'autoCreateDashboard' } });
          throw error;
        }

        if (!data?.dashboardId || !data?.redirectUrl) {
          throw new Error(
            'Invalid response: missing dashboardId or redirectUrl'
          );
        }

        return data;
      } catch (error) {
        console.error('Error auto-creating dashboard:', error);
        captureException(error, {
          tags: { context: 'autoCreateDashboard_network' },
        });
        throw error;
      }
    },
    []
  );

  return { autoCreateDashboard };
}
